// Iter 333 (Feb 2026), P3: Index-Stats Admin-Karte
// ────────────────────────────────────────────────────────────────────────
// Zeigt für jede Hot-Collection (appointments, invoices, rent_invoices,
// subscriptions, recurring_subscriptions, point_transactions, waitlist,
// customers, users) eine Liste der vorhandenen MongoDB-Indexes mit
// Nutzungs-Counter (Operationen seit MongoDB-Start) und Reset-Datum.
//
// Sinn: Die 17 in iter 329e ergänzten Compound-Indexes belegen RAM und
// verlangsamen Writes. Mit dieser Karte kann der Admin sehen welche
// Indexes tatsächlich Queries beschleunigen, und unbenutzte droppen.
//
// Backend: GET /api/admin/diagnostics/index-stats
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatKey = (key) => {
  if (!key) return '—';
  return Object.entries(key)
    .map(([k, v]) => `${k}: ${v > 0 ? '↑' : '↓'}`)
    .join(', ');
};

const formatSince = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const IndexStatsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/diagnostics/index-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error('IndexStats Error:', e);
      toast.error('Index-Statistiken konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading && !data) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-600 border-t-transparent" />
      </div>
    );
  }

  const unused = data?.unused_indexes || [];
  const totalIndexes = data?.total_indexes || 0;

  return (
    <div className="space-y-6 p-6" data-testid="index-stats-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Database size={28} />
            MongoDB Index-Statistiken
          </h1>
          <p className="text-gray-600 mt-2">
            Nutzungs-Counter aller Indexes — hilft, unbenutzte Indexes zu
            identifizieren und zu droppen, um RAM zu sparen und Writes zu
            beschleunigen.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchStats}
          disabled={loading}
          data-testid="index-stats-refresh-btn"
        >
          <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="summary-total">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Indexes gesamt</div>
            <div className="text-3xl font-bold mt-2">{totalIndexes}</div>
          </CardContent>
        </Card>
        <Card data-testid="summary-used">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Benutzt</div>
            <div className="text-3xl font-bold text-emerald-600 mt-2 flex items-center gap-2">
              <CheckCircle size={24} /> {totalIndexes - unused.length}
            </div>
          </CardContent>
        </Card>
        <Card data-testid="summary-unused">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">Unbenutzt</div>
            <div className="text-3xl font-bold text-orange-600 mt-2 flex items-center gap-2">
              <AlertTriangle size={24} /> {unused.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unused-Liste prominent */}
      {unused.length > 0 && (
        <Card className="border-orange-300 bg-orange-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-800 flex items-center gap-2">
              <AlertTriangle size={20} /> Unbenutzte Indexes (ops = 0)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-orange-700 mb-3">
              Diese Indexes wurden seit dem letzten MongoDB-Start nicht
              genutzt. Wenn die App seit mindestens 7 Tagen produktiv läuft
              und die typischen Queries ausgeführt wurden, sind sie
              wahrscheinlich überflüssig und können gedroppt werden.
            </p>
            <div className="flex flex-wrap gap-2">
              {unused.map((u, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="bg-white border-orange-300 text-orange-700"
                  data-testid={`unused-idx-${u.collection}-${u.name}`}
                >
                  {u.collection} · {u.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Collection Tabellen */}
      {(data?.collections || []).map((coll) => (
        <Card key={coll.name} data-testid={`coll-${coll.name}`}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-mono">{coll.name}</CardTitle>
            <Badge variant="secondary">
              {coll.indexes?.length || 0} Indexes
            </Badge>
          </CardHeader>
          <CardContent>
            {coll.error ? (
              <p className="text-red-600 text-sm">Fehler: {coll.error}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500 border-b">
                    <tr>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Felder</th>
                      <th className="py-2 pr-4 text-right">Operationen</th>
                      <th className="py-2 pr-4">Seit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(coll.indexes || []).map((idx) => {
                      const isSystemIdx = idx.name === '_id_';
                      const isUnused = !isSystemIdx && idx.ops === 0;
                      return (
                        <tr
                          key={idx.name}
                          className={`border-b ${
                            isUnused ? 'bg-orange-50' : ''
                          }`}
                        >
                          <td className="py-2 pr-4 font-mono">
                            {idx.name}
                            {isSystemIdx && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                system
                              </Badge>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-gray-600">
                            {formatKey(idx.key)}
                          </td>
                          <td className="py-2 pr-4 text-right font-mono font-semibold">
                            {idx.ops.toLocaleString('de-CH')}
                          </td>
                          <td className="py-2 pr-4 text-gray-500">
                            {formatSince(idx.since)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default IndexStatsPage;
